import {
  computeBDR,
  computeDiffPct10dp,
  computeDiffValue,
  computeQAS,
} from '../../computations/labComputations'
import { fmt, fmtPct } from '../../../../robFormatUtils'
import { useFormContext, useWatch } from 'react-hook-form'
import PropTypes from 'prop-types'
import React from 'react'
import { useTranslation } from 'react-i18next'

import './labDifferenceSection.less'

const ReadOnlyCell = ({ value }) => (
  <span className="lab-diff__value">{value}</span>
)

ReadOnlyCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

const LabDifferenceSection = () => {
  const { t } = useTranslation()
  const { control } = useFormContext()

  const bdnDensity15 = useWatch({ control, name: 'bunker.bdn.density15' })
  const bdnVolume15 = useWatch({ control, name: 'bunker.bdn.volume15' })
  const bargeVolume15 = useWatch({ control, name: 'bunker.barge.volume15' })
  const labDensity15 = useWatch({ control, name: 'laboratory.tests.density15' })

  const bdrValues = computeBDR(bdnDensity15, bdnVolume15)
  const qasValues = computeQAS(labDensity15, bargeVolume15, bdnVolume15)

  // Volume: null → 0 for both operands
  const volDiff = computeDiffValue(bargeVolume15, bdnVolume15, {
    nullAsZero: true,
  })
  const volPct = computeDiffPct10dp(bargeVolume15, bdnVolume15, {
    nullAsZero: true,
  })

  // Density, Mass, Weight: null → null (blank)
  const denDiff = computeDiffValue(labDensity15, bdnDensity15)
  const denPct = computeDiffPct10dp(labDensity15, bdnDensity15)

  const massDiff = computeDiffValue(qasValues?.massMtVac, bdrValues?.massMtVac)
  const massPct = computeDiffPct10dp(qasValues?.massMtVac, bdrValues?.massMtVac)

  const weightDiff = computeDiffValue(
    qasValues?.weightMtAir,
    bdrValues?.weightMtAir
  )
  const weightPct = computeDiffPct10dp(
    qasValues?.weightMtAir,
    bdrValues?.weightMtAir
  )

  const DIFF_ROWS = [
    {
      diffValue: fmt(volDiff),
      labelKey: 'rob.survey.bunker.lab.difference.volume15',
      pctValue: fmtPct(volPct),
    },
    {
      diffValue: fmt(denDiff),
      labelKey: 'rob.survey.bunker.lab.difference.density15',
      pctValue: fmtPct(denPct),
    },
    {
      diffValue: fmt(massDiff),
      labelKey: 'rob.survey.bunker.lab.difference.massMtVac',
      pctValue: fmtPct(massPct),
    },
    {
      diffValue: fmt(weightDiff),
      labelKey: 'rob.survey.bunker.lab.difference.weightMtAir',
      pctValue: fmtPct(weightPct),
    },
  ]

  return (
    <div className="lab-card">
      {/* Three-column header: title | Value | Percent % */}
      <div className="lab-card__header lab-diff-header">
        <span className="lab-diff-header__title">
          {t('rob.survey.bunker.lab.difference.title')}
        </span>
        <span className="lab-diff-header__col">
          {t('rob.survey.bunker.lab.difference.valueHeader')}
        </span>
        <span className="lab-diff-header__col">
          {t('rob.survey.bunker.lab.difference.percentHeader')}
        </span>
      </div>

      <div className="lab-card__body">
        {DIFF_ROWS.map(({ labelKey, diffValue, pctValue }) => (
          <div className="lab-diff__row" key={labelKey}>
            <span className="lab-diff__label">{t(labelKey)}</span>
            <ReadOnlyCell value={diffValue} />
            <ReadOnlyCell value={pctValue} />
          </div>
        ))}
      </div>
    </div>
  )
}

export default LabDifferenceSection
